// src/pages/Admin/AdminCaseForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { casesAPI } from '../../api/cases.js';
import { productsAPI } from '../../api/products.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';

export const AdminCaseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const isEdit = id && id !== 'new';
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    full_description: '',
    client: '',
    category: 'outdoor',
    cover_image: '',
    gallery_images: [],
    budget: '',
    completed_at: '',
    is_completed: false,
    is_featured: false,
    tags: [],
    results: {},
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [resultsString, setResultsString] = useState('{}');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [productsRes] = await Promise.all([
        productsAPI.getAll({ is_active: true, limit: 100 }),
      ]);
      setProducts(productsRes.data.data || []);

      if (isEdit) {
        const caseRes = await casesAPI.getById(id);
        const caseItem = caseRes.data.data;
        setFormData({
          title: caseItem.title || '',
          slug: caseItem.slug || '',
          description: caseItem.description || '',
          full_description: caseItem.full_description || '',
          client: caseItem.client || '',
          category: caseItem.category || 'outdoor',
          cover_image: caseItem.cover_image || '',
          gallery_images: caseItem.gallery_images || [],
          budget: caseItem.budget || '',
          completed_at: caseItem.completed_at ? caseItem.completed_at.split('T')[0] : '',
          is_completed: caseItem.is_completed || false,
          is_featured: caseItem.is_featured || false,
          tags: caseItem.tags || [],
          results: caseItem.results || {},
        });
        setResultsString(JSON.stringify(caseItem.results || {}, null, 2));
        setSelectedProducts(caseItem.Products?.map(p => p.id) || []);
      }
    } catch (err) {
      error('Не удалось загрузить данные');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Проверяем валидность JSON для результатов
    try {
      JSON.parse(resultsString);
    } catch {
      error('Невалидный JSON в результатах');
      return;
    }

    if (!formData.title || !formData.slug || !formData.client || !formData.category) {
      error('Заполните все обязательные поля');
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        results: JSON.parse(resultsString),
        product_ids: selectedProducts,
      };

      if (isEdit) {
        await casesAPI.update(id, submitData);
        success('Кейс обновлен');
      } else {
        await casesAPI.create(submitData);
        success('Кейс создан');
      }
      navigate('/admin/cases');
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось сохранить кейс');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGalleryChange = (value) => {
    const images = value.split('\n').filter(url => url.trim());
    setFormData({ ...formData, gallery_images: images });
  };

  if (isLoading && isEdit) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;
  }

  return (
    <div style={{
      backgroundColor: 'rgb(var(--c-surface))',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <form onSubmit={handleSubmit}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>
          {isEdit ? 'Редактировать кейс' : 'Новый кейс'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="Название *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <Input
            label="Slug *"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
          <Input
            label="Клиент *"
            value={formData.client}
            onChange={(e) => setFormData({ ...formData, client: e.target.value })}
            required
          />
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Категория *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
              }}
              required
            >
              <option value="digital">Цифровые</option>
              <option value="print">Печать</option>
              <option value="video">Видео</option>
              <option value="social">Соцсети</option>
              <option value="outdoor">Наружка</option>
            </select>
          </div>
        </div>

        <div style={{ height: '12px' }} />

        <Input
          label="Краткое описание"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            Полное описание
          </label>
          <textarea
            value={formData.full_description}
            onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
              minHeight: '120px',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ height: '12px' }} />

        <Input
          label="URL обложки"
          value={formData.cover_image}
          onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
        />
        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            Галерея изображений (по одному URL на строку)
          </label>
          <textarea
            value={formData.gallery_images.join('\n')}
            onChange={(e) => handleGalleryChange(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
              minHeight: '80px',
              fontFamily: 'monospace',
            }}
            placeholder="https://example.com/image1.jpg"
          />
        </div>

        <div style={{ height: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="Бюджет"
            type="number"
            value={formData.budget}
            onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
          />
          <Input
            label="Дата завершения"
            type="date"
            value={formData.completed_at}
            onChange={(e) => setFormData({ ...formData, completed_at: e.target.value })}
          />
        </div>

        <div style={{ height: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.is_completed}
              onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
            />
            Завершен
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
            />
            Рекомендованный
          </label>
        </div>

        <div style={{ height: '12px' }} />

        <Input
          label="Теги (через запятую)"
          value={formData.tags}
          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          placeholder="наружка, вывеска, световые буквы"
        />

        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            Результаты (JSON)
          </label>
          <textarea
            value={resultsString}
            onChange={(e) => setResultsString(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
              minHeight: '100px',
              fontFamily: 'monospace',
              backgroundColor: 'rgb(var(--c-bg))',
            }}
          />
          <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', marginTop: '4px' }}>
            Пример: {"{ \"sales_growth\": \"+30%\", \"new_clients\": \"+50\" }"}
          </div>
        </div>

        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            Связанные товары
          </label>
          <select
            multiple
            value={selectedProducts}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => option.value);
              setSelectedProducts(values);
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              minHeight: '100px',
            }}
          >
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.sku})
              </option>
            ))}
          </select>
          <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', marginTop: '4px' }}>
            Hold Ctrl (Cmd) для выбора нескольких
          </div>
        </div>

        <div style={{ height: '24px' }} />

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button type="submit" isLoading={isLoading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/cases')}>
            Отмена
          </Button>
        </div>
      </form>
    </div>
  );
};